import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useToast } from "../../components/Toast";
import { useThemeColor } from "../../hooks/useThemeColor";
import { useTranslation } from "../../i18n/LanguageContext";
import {
  GripVertical,
  Pencil,
  Trash2,
  ChevronUp,
  ChevronDown,
  HelpCircle,
  X
} from "lucide-react";
import { BASE_FILEURL } from '../../components/constants';
import { authHeaders } from '../../utils/authHeaders';
import DeleteModal from '../../components/atomic/DeleteModal';

const API_URL = BASE_FILEURL;

const Faqs = () => {
  const [faqs, setFaqs] = useState([]);
  const [originalFaqs, setOriginalFaqs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [form, setForm] = useState({ question: "", answer: "", media_blob_path: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [mediaLoading, setMediaLoading] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  const fileInputRef = useRef(null);
  const { aispaceColor } = useThemeColor();
  const { t } = useTranslation();
  const { data } = useSelector((state) => state.admin);
  const { addToast, ToastContainer } = useToast();
  const isSuperAdmin = data?.user_role === "SUPER_ADMIN";

  useEffect(() => {
    fetchFaqs();
  }, []);

  // Track changes
  useEffect(() => {
    if (originalFaqs.length > 0) {
      const changed = JSON.stringify(faqs) !== JSON.stringify(originalFaqs);
      setHasChanges(changed);
    }
  }, [faqs, originalFaqs]);

  const fetchFaqs = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_URL}/faq/`, authHeaders());
      setFaqs(res.data);
      setOriginalFaqs(res.data);
    } catch (error) {
      addToast(t('helpCenter.faqToastFetchError'), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (faq) => {
    setEditing(faq.id);
    setForm({ question: faq.question, answer: faq.answer, media_blob_path: faq.media_blob_path });
    setPanelOpen(true);
  };

  const handleAdd = () => {
    setEditing(null);
    setForm({ question: "", answer: "", media_blob_path: "" });
    setPanelOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/faq/${id}`, authHeaders());
      addToast(t('helpCenter.faqToastDeleted'), "success");
      fetchFaqs();
      setDeleteConfirm(null);
    } catch (error) {
      addToast(t('helpCenter.faqToastDeleteError'), "error");
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (editing) formData.append("faq_id", editing);
      const res = await axios.post(`${API_URL}/faq/upload`, formData, authHeaders());
      setForm((prev) => ({ ...prev, media_blob_path: res.data.blob_path }));
      addToast(t('helpCenter.faqToastFileUploaded'), "success");
    } catch (error) {
      addToast(t('helpCenter.faqToastUploadError'), "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDraggingFile(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.type.startsWith('image/') || file.type.startsWith('video/'))) {
      handleFileUpload(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleSubmit = async () => {
    if (!form.question.trim() || !form.answer.trim()) {
      addToast(t('helpCenter.faqValidationRequired'), "error");
      return;
    }
    if (isUploading) {
      addToast(t('helpCenter.faqValidationUploadWait'), "error");
      return;
    }
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("question", form.question);
      formData.append("answer", form.answer);
      formData.append("media_blob_path", form.media_blob_path || "");
      if (editing) {
        await axios.put(`${API_URL}/faq/${editing}`, formData, authHeaders());
        addToast(t('helpCenter.faqToastUpdated'), "success");
      } else {
        await axios.post(`${API_URL}/faq/`, formData, authHeaders());
        addToast(t('helpCenter.faqToastCreated'), "success");
      }
      closePanel();
      fetchFaqs();
    } catch (error) {
      addToast(t('helpCenter.faqToastSaveError'), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const closePanel = () => {
    setPanelOpen(false);
    setForm({ question: "", answer: "", media_blob_path: "" });
    setEditing(null);
  };

  const handleCancel = () => {
    setFaqs([...originalFaqs]);
    setHasChanges(false);
  };

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);
      setOriginalFaqs([...faqs]);
      setHasChanges(false);
      addToast(t('helpCenter.faqToastChangesSaved'), "success");
    } catch (error) {
      addToast(t('helpCenter.faqToastChangesSaveError'), "error");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleExpand = (faqId) => {
    setExpandedFaq(expandedFaq === faqId ? null : faqId);
  };

  // Drag and drop handlers for reordering
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverItem(index);
  };

  const handleDragEnd = () => {
    if (draggedItem !== null && dragOverItem !== null && draggedItem !== dragOverItem) {
      const newFaqs = [...faqs];
      const draggedFaq = newFaqs[draggedItem];
      newFaqs.splice(draggedItem, 1);
      newFaqs.splice(dragOverItem, 0, draggedFaq);
      setFaqs(newFaqs);
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const renderMedia = (faq) => {
    if (!faq.media_blob_path) return null;
    const mediaUrl = `${API_URL}/faq/media/${faq.id}`;
    const isVideo = faq.media_blob_path.match(/\.(mp4|webm)$/i);
    const isLoadingMedia = mediaLoading[faq.id];
    return (
      <div className="w-full max-w-md rounded-lg border border-[#E5E7EB] min-h-[180px] flex items-center justify-center relative bg-white mt-3">
        {isLoadingMedia && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-white bg-opacity-60 rounded-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: aispaceColor }}></div>
          </div>
        )}
        {isVideo ? (
          <video
            src={mediaUrl}
            controls
            className="w-full h-auto rounded-lg object-cover"
            style={{ display: isLoadingMedia ? 'none' : 'block' }}
            onLoadStart={() => setMediaLoading((prev) => ({ ...prev, [faq.id]: true }))}
            onLoadedData={() => setMediaLoading((prev) => ({ ...prev, [faq.id]: false }))}
          />
        ) : (
          <img
            src={mediaUrl}
            alt={t('helpCenter.faqMediaAlt')}
            className="w-full h-auto rounded-lg object-cover"
            style={{ display: isLoadingMedia ? 'none' : 'block' }}
            onLoad={() => setMediaLoading((prev) => ({ ...prev, [faq.id]: false }))}
            onLoadStart={() => setMediaLoading((prev) => ({ ...prev, [faq.id]: true }))}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <ToastContainer />

      {/* Header Section */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-[20px] font-semibold text-[#111827] mb-1">{t('helpCenter.faqPageTitle')}</h2>
          <p className="text-[14px] text-[#6B7280]">
            {t('helpCenter.faqPageSubtitle')}
          </p>
        </div>
      </div>

      {/* Manage FAQs Card */}
      <div className="bg-[#F5F5F5] border border-[#EEEEEE] rounded-xl p-6">
        {/* Card Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-[16px] font-semibold text-[#111827]">{t('helpCenter.faqManageTitle')}</h3>
          {isSuperAdmin && (
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium text-white rounded-lg transition-colors hover:opacity-90"
              style={{ backgroundColor: aispaceColor }}
            >
              {t('helpCenter.faqAddButton')}
            </button>
          )}
        </div>

        {/* FAQ List */}
        <div className="space-y-3">
          {isLoading && faqs.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: aispaceColor }}></div>
            </div>
          ) : faqs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              {/* Empty state */}
              <div className="relative mb-6">
                <div className="w-[120px] h-[120px] rounded-full border-2 border-dashed border-[#E5E7EB] flex items-center justify-center relative">
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#E5E7EB]"></div>
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#E5E7EB]"></div>
                  <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 rounded-full bg-[#E5E7EB]"></div>
                  <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-2 h-2 rounded-full bg-[#E5E7EB]"></div>
                  <div className="w-16 h-16 rounded-full bg-[#F9FAFB] border border-[#E5E7EB] flex items-center justify-center">
                    <HelpCircle size={28} className="text-[#9CA3AF]" />
                  </div>
                </div>
              </div>
              <h3 className="text-[16px] font-semibold text-[#111827] mb-2">{t('helpCenter.faqEmptyTitle')}</h3>
              <p className="text-[14px] text-[#6B7280]">{t('helpCenter.faqEmptySubtitle')}</p>
            </div>
          ) : (
            faqs.map((faq, index) => {
              const isExpanded = expandedFaq === faq.id;
              const isDragging = draggedItem === index;
              const isDragOver = dragOverItem === index;

              return (
                <div
                  key={faq.id}
                  draggable={isSuperAdmin}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg transition-all ${
                    isDragging ? 'opacity-50' : ''
                  } ${isDragOver ? 'border-2' : ''}`}
                  style={{ borderColor: isDragOver ? aispaceColor : undefined }}
                >
                  {/* FAQ Header */}
                  <div className="flex items-center gap-3 p-4">
                    {/* Drag Handle */}
                    {isSuperAdmin && (
                      <div className="flex-shrink-0 cursor-grab active:cursor-grabbing">
                        <GripVertical size={20} className="text-[#9CA3AF]" />
                      </div>
                    )}

                    {/* Question Content */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-[14px] font-semibold text-[#111827] leading-relaxed">
                        {faq.question}
                      </h4>

                      {/* Expanded Answer */}
                      {isExpanded && (
                        <div className="mt-1">
                          <p className="text-[14px] text-[#616161] leading-relaxed whitespace-pre-line">
                            {faq.answer}
                          </p>
                          {faq.media_blob_path && renderMedia(faq)}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {isSuperAdmin && (
                        <>
                          <button
                            onClick={() => handleEdit(faq)}
                            className="p-2 text-[#9CA3AF] hover:text-[#6B7280] hover:bg-[#F3F4F6] rounded-lg transition-colors"
                          >
                            <Pencil size={18} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(faq.id)}
                            className="p-2 text-[#9CA3AF] hover:text-[#EF4444] hover:bg-[#FEF2F2] rounded-lg transition-colors"
                          >
                            <Trash2 size={18} />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => toggleExpand(faq.id)}
                        className="p-2 text-[#9CA3AF] hover:text-[#6B7280] hover:bg-[#F3F4F6] rounded-lg transition-colors"
                      >
                        {isExpanded ? (
                          <ChevronUp size={18} />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onDelete={() => handleDelete(deleteConfirm)}
        title={t('helpCenter.faqDeleteTitle')}
        message={t('helpCenter.faqDeleteMessage')}
      />

      {/* Slide-in Panel from Right */}
      {panelOpen && (
        <div className="fixed inset-0 z-[200]">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30 transition-opacity"
            onClick={closePanel}
          />

          {/* Panel */}
          <div className="absolute top-0 right-0 h-full w-[400px] bg-white shadow-2xl flex flex-col animate-slide-in-right">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E7EB]">
              <h2 className="text-[18px] font-semibold text-[#111827]">
                {editing ? t('helpCenter.faqDrawerEditTitle') : t('helpCenter.faqDrawerAddTitle')}
              </h2>
              <button
                onClick={closePanel}
                className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors"
              >
                <X size={20} className="text-[#6B7280]" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="space-y-6">
                {/* Question Field */}
                <div>
                  <label className="block text-[14px] font-medium text-[#111827] mb-2">
                    {t('helpCenter.faqQuestionLabel')}
                  </label>
                  <input
                    type="text"
                    value={form.question}
                    onChange={(e) => setForm({ ...form, question: e.target.value })}
                    placeholder={t('helpCenter.faqQuestionPlaceholder')}
                    className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-0 transition-colors"
                    onFocus={(e) => e.target.style.borderColor = aispaceColor}
                    onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  />
                </div>

                {/* Answer Field */}
                <div>
                  <label className="block text-[14px] font-medium text-[#111827] mb-2">
                    {t('helpCenter.faqAnswerLabel')}
                  </label>
                  <textarea
                    value={form.answer}
                    onChange={(e) => setForm({ ...form, answer: e.target.value })}
                    placeholder={t('helpCenter.faqAnswerPlaceholder')}
                    rows={5}
                    className="w-full px-4 py-3 text-[14px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 transition-colors resize-none"
                    style={{ '--tw-ring-color': aispaceColor }}
                  />
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-[14px] font-medium text-[#111827] mb-2">
                    {t('helpCenter.faqMediaLabel')}
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*,video/*"
                    className="hidden"
                  />
                  <div
                    onDragOver={(e) => { e.preventDefault(); setIsDraggingFile(true); }}
                    onDragLeave={() => setIsDraggingFile(false)}
                    onDrop={handleFileDrop}
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                      isDraggingFile ? 'border-opacity-100 bg-opacity-10' : 'border-[#E5E7EB]'
                    }`}
                    style={{
                      borderColor: isDraggingFile ? aispaceColor : undefined,
                      backgroundColor: isDraggingFile ? `${aispaceColor}10` : undefined
                    }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {isUploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: aispaceColor }}></div>
                        <p className="text-[14px] text-[#6B7280]">{t('helpCenter.faqUploading')}</p>
                      </div>
                    ) : form.media_blob_path ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <p className="text-[14px] text-[#374151]">{t('helpCenter.faqFileUploaded')}</p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setForm({ ...form, media_blob_path: "" });
                          }}
                          className="text-[13px] text-[#EF4444] hover:underline"
                        >
                          {t('helpCenter.faqRemoveFile')}
                        </button>
                      </div>
                    ) : (
                      (() => {
                        const BROWSE_TOKEN = '__BROWSE__';
                        const parts = t('helpCenter.faqDropHint', { browse: BROWSE_TOKEN }).split(BROWSE_TOKEN);
                        return (
                          <p className="text-[14px] text-[#6B7280]">
                            {parts[0]}
                            <span className="font-medium" style={{ color: aispaceColor }}>{t('helpCenter.faqBrowse')}</span>
                            {parts[1]}
                          </p>
                        );
                      })()
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-3 px-6 py-4 border-t border-[#E5E7EB]">
              <button
                onClick={closePanel}
                className="flex-1 px-4 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={isLoading || isUploading}
                className="flex-1 px-4 py-2.5 text-[14px] font-medium text-white rounded-lg transition-colors hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: aispaceColor }}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('helpCenter.faqSaving')}
                  </>
                ) : (
                  t('common.save')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add animation styles */}
      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Faqs;
